import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, getErrorMessage } from '../lib/api';
import { Breadcrumbs, BreadcrumbItem } from '../components/ui';
import { alert, toast } from '../utils/toast';
import type { IntegrationRoute } from '../types';
import { ArrowLeft, CheckCircle2, FlaskConical, Network } from 'lucide-react';

export const IntegrationRouteEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditing = !!id;

  const [formData, setFormData] = useState({
    name: '',
    endpoint_path: '',
    external_url: '',
    http_method: 'GET' as 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    auth_method: 'api_key' as 'api_key' | 'oauth2' | 'basic' | 'custom',
    auth_config: {} as Record<string, unknown>,
    timeout_ms: 30000,
    retry_count: 3,
    strict_mode: false,
    fallback_response: {} as Record<string, unknown>,
  });

  const [authConfigText, setAuthConfigText] = useState('{\n  \n}');
  const [fallbackResponseText, setFallbackResponseText] = useState('{\n  \n}');
  const [testPayload, setTestPayload] = useState('{}');

  const { data: route } = useQuery({
    queryKey: ['integration-route', id],
    queryFn: async () => {
      const response = await api.get<IntegrationRoute>(`/admin/integration-routes/${id}`);
      return response.data;
    },
    enabled: isEditing,
  });

  useEffect(() => {
    if (route) {
      setFormData({
        name: route.name,
        endpoint_path: route.endpoint_path,
        external_url: route.external_url,
        http_method: route.http_method,
        auth_method: route.auth_method,
        auth_config: route.auth_config || {},
        timeout_ms: route.timeout_ms,
        retry_count: route.retry_count,
        strict_mode: route.strict_mode,
        fallback_response: route.fallback_response || {},
      });
      setAuthConfigText(JSON.stringify(route.auth_config || {}, null, 2));
      setFallbackResponseText(JSON.stringify(route.fallback_response || {}, null, 2));
    }
  }, [route]);

  const saveMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      if (isEditing) {
        const response = await api.put(`/admin/integration-routes/${id}`, data);
        return response.data;
      } else {
        const response = await api.post('/admin/integration-routes', data);
        return response.data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integration-routes'] });
      void alert.success(
        isEditing ? 'Atualizado!' : 'Criado!',
        isEditing ? 'Rota atualizada com sucesso!' : 'Rota criada com sucesso!'
      );
      navigate('/integration-routes');
    },
  });

  const testMutation = useMutation({
    mutationFn: async (payload: string) => {
      const response = await api.post(`/admin/integration-routes/${id}/test`, {
        payload: JSON.parse(payload),
      });
      return response.data;
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    let parsedAuthConfig = formData.auth_config;
    let parsedFallback = formData.fallback_response;
    try {
      parsedAuthConfig = authConfigText.trim() ? JSON.parse(authConfigText) : {};
    } catch {
      void alert.error('JSON Inválido', 'A configuração de autenticação contém JSON inválido.');
      return;
    }
    try {
      parsedFallback = fallbackResponseText.trim() ? JSON.parse(fallbackResponseText) : {};
    } catch {
      void alert.error('JSON Inválido', 'A resposta alternativa (fallback) contém JSON inválido.');
      return;
    }
    saveMutation.mutate({
      ...formData,
      auth_config: parsedAuthConfig,
      fallback_response: parsedFallback,
    });
  };

  const handleTest = () => {
    if (!id) {
      toast.warning('Por favor, guarde a rota primeiro antes de a testar.');
      return;
    }
    try {
      JSON.parse(testPayload);
      testMutation.mutate(testPayload);
    } catch {
      void alert.error('Erro', 'Payload JSON inválido');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <Breadcrumbs>
        <BreadcrumbItem href="/">Início</BreadcrumbItem>
        <BreadcrumbItem href="/integration-routes">Rotas de Integração</BreadcrumbItem>
        <BreadcrumbItem>{isEditing ? 'Editar Rota' : 'Nova Rota'}</BreadcrumbItem>
      </Breadcrumbs>

      <div>
        <Link
          to="/integration-routes"
          className="inline-flex items-center gap-1.5 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm mb-3 font-semibold transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Voltar para as rotas</span>
        </Link>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2.5">
          <Network className="w-8 h-8 text-primary-500" />
          <span>{isEditing ? 'Editar Rota de Integração' : 'Criar Rota de Integração'}</span>
        </h1>
        <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm sm:text-base">
          Configure a integração com uma API externa
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {saveMutation.isError && (
          <div className="rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/50 p-4">
            <p className="text-sm text-rose-800 dark:text-rose-300">
              {getErrorMessage(saveMutation.error)}
            </p>
          </div>
        )}

        <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200 dark:border-slate-800 shadow-xs">
          <h2 className="text-xl font-bold mb-4 text-slate-900 dark:text-white">
            Informação Básica
          </h2>
          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="label-modern">
                Nome da Rota *
              </label>
              <input
                id="name"
                type="text"
                required
                className="input-modern"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="endpoint_path" className="label-modern">
                  Caminho do Endpoint *
                </label>
                <input
                  id="endpoint_path"
                  type="text"
                  required
                  placeholder="/api/v1/recurso"
                  className="input-modern font-mono"
                  value={formData.endpoint_path}
                  onChange={(e) => setFormData({ ...formData, endpoint_path: e.target.value })}
                />
              </div>

              <div>
                <label htmlFor="http_method" className="label-modern">
                  Método HTTP *
                </label>
                <select
                  id="http_method"
                  required
                  className="input-modern"
                  value={formData.http_method}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      http_method: e.target.value as typeof formData.http_method,
                    })
                  }
                >
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="DELETE">DELETE</option>
                  <option value="PATCH">PATCH</option>
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="external_url" className="label-modern">
                URL Externa *
              </label>
              <input
                id="external_url"
                type="url"
                required
                placeholder="https://api.exemplo.com/endpoint"
                className="input-modern font-mono"
                value={formData.external_url}
                onChange={(e) => setFormData({ ...formData, external_url: e.target.value })}
              />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200 dark:border-slate-800 shadow-xs">
          <h2 className="text-xl font-bold mb-4 text-slate-900 dark:text-white">Autenticação</h2>
          <div className="space-y-4">
            <div>
              <label htmlFor="auth_method" className="label-modern">
                Método de Autenticação *
              </label>
              <select
                id="auth_method"
                required
                className="input-modern"
                value={formData.auth_method}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    auth_method: e.target.value as typeof formData.auth_method,
                  })
                }
              >
                <option value="api_key">API Key</option>
                <option value="oauth2">OAuth2</option>
                <option value="basic">Basic Auth</option>
                <option value="custom">Custom Headers</option>
              </select>
            </div>

            <div>
              <label htmlFor="auth_config" className="label-modern">
                Configuração de Autenticação (JSON)
              </label>
              <textarea
                id="auth_config"
                className="input-modern font-mono text-xs"
                rows={5}
                placeholder='{"api_key": "a-sua-chave", "header": "X-API-Key"}'
                value={authConfigText}
                onChange={(e) => setAuthConfigText(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200 dark:border-slate-800 shadow-xs">
          <h2 className="text-xl font-bold mb-4 text-slate-900 dark:text-white">
            Definições Avançadas
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="timeout_ms" className="label-modern">
                Timeout (ms) *
              </label>
              <input
                id="timeout_ms"
                type="number"
                required
                min="1000"
                max="60000"
                className="input-modern"
                value={formData.timeout_ms}
                onChange={(e) => setFormData({ ...formData, timeout_ms: parseInt(e.target.value) })}
              />
            </div>

            <div>
              <label htmlFor="retry_count" className="label-modern">
                Número de Tentativas (Retries) *
              </label>
              <input
                id="retry_count"
                type="number"
                required
                min="0"
                max="5"
                className="input-modern"
                value={formData.retry_count}
                onChange={(e) =>
                  setFormData({ ...formData, retry_count: parseInt(e.target.value) })
                }
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="flex items-center cursor-pointer gap-2">
              <input
                type="checkbox"
                className="w-4 h-4 rounded border-slate-300 dark:border-slate-700 text-primary-600 focus:ring-primary-500"
                checked={formData.strict_mode}
                onChange={(e) => setFormData({ ...formData, strict_mode: e.target.checked })}
              />
              <span className="text-sm text-slate-700 dark:text-slate-300 font-medium">
                Modo Estrito (apenas incluir campos mapeados no output da resposta)
              </span>
            </label>
          </div>

          <div className="mt-4">
            <label htmlFor="fallback_response" className="label-modern">
              Resposta Alternativa (Fallback) (JSON)
            </label>
            <textarea
              id="fallback_response"
              className="input-modern font-mono text-xs"
              rows={5}
              placeholder='{"error": "Serviço temporariamente indisponível"}'
              value={fallbackResponseText}
              onChange={(e) => setFallbackResponseText(e.target.value)}
            />
          </div>
        </div>

        {isEditing && (
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200 dark:border-slate-800 shadow-xs">
            <h2 className="text-xl font-bold mb-4 text-slate-900 dark:text-white flex items-center gap-2">
              <FlaskConical className="w-5 h-5 text-primary-500" />
              <span>Testar Rota</span>
            </h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="test_payload" className="label-modern">
                  Payload de Teste (JSON)
                </label>
                <textarea
                  id="test_payload"
                  className="input-modern font-mono text-xs"
                  rows={8}
                  value={testPayload}
                  onChange={(e) => setTestPayload(e.target.value)}
                />
              </div>

              {testMutation.isSuccess && (
                <div className="rounded-xl bg-emerald-50 dark:bg-emerald-950/30 p-4 border border-emerald-200 dark:border-emerald-800/50">
                  <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-300 mb-2 flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Teste Efetuado com Sucesso</span>
                  </p>
                  <pre className="text-xs text-emerald-700 dark:text-emerald-200 overflow-x-auto font-mono">
                    {JSON.stringify(testMutation.data, null, 2)}
                  </pre>
                </div>
              )}

              {testMutation.isError && (
                <div className="rounded-xl bg-rose-50 dark:bg-rose-950/40 p-4 border border-rose-200 dark:border-rose-900/50">
                  <p className="text-sm text-rose-800 dark:text-rose-300">
                    {getErrorMessage(testMutation.error)}
                  </p>
                </div>
              )}

              <button
                type="button"
                onClick={handleTest}
                disabled={testMutation.isPending}
                className="btn-base btn-md btn-secondary"
              >
                {testMutation.isPending ? 'A testar...' : 'Testar Rota'}
              </button>
            </div>
          </div>
        )}

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/integration-routes')}
            className="btn-base btn-md btn-secondary"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saveMutation.isPending}
            className="btn-base btn-md btn-primary"
          >
            {saveMutation.isPending ? 'A guardar...' : isEditing ? 'Atualizar Rota' : 'Criar Rota'}
          </button>
        </div>
      </form>
    </div>
  );
};
export default IntegrationRouteEditor;
